import {ReactNode, useEffect, useLayoutEffect, useReducer, useRef} from 'react';

import {Observability} from '@stripe-internal/conversations-client';

import {AppCloseHandler, AppConfig, AppProfile} from '../types';
import {AppContext} from '../contexts/AppContext';
import {AppImpl} from '../App';
import {ClientFetch, defaultClientFetch} from '../lib/util/clientFetch';
import {ConversationsUIProviders} from './ConversationsUIProviders';
import {ParentMessageBus} from '../lib/parentMessageBus/ParentMessageBus';

type Props = {
  config: AppConfig;
  profile: AppProfile;
  clientFetch?: ClientFetch;
  parentMessageBus?: ParentMessageBus;
  observability?: Partial<Observability>;
  children: ReactNode;
  onClose?: AppCloseHandler;
};

type StableAppProps = Omit<Props, 'children' | 'clientFetch'> & {
  clientFetch: ClientFetch;
};

function useStableApp({
  config,
  profile,
  clientFetch,
  parentMessageBus,
  observability,
  onClose,
}: StableAppProps): AppImpl | null {
  const appRef = useRef<AppImpl | null>(null);
  const [, forceRender] = useReducer((count) => count + 1, 0);
  const lastPropsRef = useRef({
    config,
    profile,
    clientFetch,
    parentMessageBus,
    onClose,
    observability,
  });

  useLayoutEffect(() => {
    if (appRef.current) {
      return;
    }

    appRef.current = new AppImpl(
      lastPropsRef.current.config,
      lastPropsRef.current.profile,
      lastPropsRef.current.clientFetch,
      lastPropsRef.current.parentMessageBus,
      lastPropsRef.current.onClose,
      lastPropsRef.current.observability,
    );
    forceRender();
  }, []);

  const app = appRef.current;

  useEffect(() => {
    if (!app) {
      return;
    }

    app.init();
    return () => {
      app.destroy();
    };
  }, [app]);

  // Compare against the last applied props so A -> B -> A updates are not skipped,
  // but skips initial props since app has them on initialization
  useEffect(() => {
    if (!app || Object.is(config, lastPropsRef.current.config)) {
      return;
    }

    app.updateConfig(config);
    lastPropsRef.current.config = config;
  }, [app, config]);

  useEffect(() => {
    if (!app || Object.is(profile, lastPropsRef.current.profile)) {
      return;
    }

    app.updateProfile(profile);
    lastPropsRef.current.profile = profile;
  }, [app, profile]);

  useEffect(() => {
    if (!app || Object.is(onClose, lastPropsRef.current.onClose)) {
      return;
    }

    app.updateOnClose(onClose);
    lastPropsRef.current.onClose = onClose;
  }, [app, onClose]);

  useEffect(() => {
    if (
      !app ||
      Object.is(parentMessageBus, lastPropsRef.current.parentMessageBus)
    ) {
      return;
    }

    app.updateParentMessageBus(parentMessageBus);
    lastPropsRef.current.parentMessageBus = parentMessageBus;
  }, [app, parentMessageBus]);

  return app;
}

export function AppProvider({
  config,
  profile,
  clientFetch = defaultClientFetch(),
  parentMessageBus,
  observability,
  onClose,
  children,
}: Props) {
  const app = useStableApp({
    config,
    profile,
    clientFetch,
    parentMessageBus,
    observability,
    onClose,
  });

  if (!app) {
    return null;
  }

  return (
    <AppContext.Provider value={app}>
      <ConversationsUIProviders>{children}</ConversationsUIProviders>
    </AppContext.Provider>
  );
}
